/**
 * UpdateGate — applies a pending live update at safe moments.
 *
 * Mounted inside the Router. When versionWatch detects that a newer deploy is
 * live ("out of sync"):
 *   - the update is applied automatically on the NEXT route navigation (a full
 *     reload of the new URL — the page was changing anyway, so nothing is lost)
 *   - meanwhile a small pill offers "Update now" for users who want it
 *     immediately.
 *
 * Renders nothing when the app is up to date.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  isUpdatePending,
  applyUpdateNow,
  UPDATE_PENDING_EVENT,
} from '../../utils/versionWatch';

export const UpdateGate: React.FC = () => {
  const location = useLocation();
  const [pending, setPending] = useState<boolean>(isUpdatePending());
  const prevPathRef = useRef(location.pathname);

  // Learn about a newly detected deploy.
  useEffect(() => {
    const onPending = () => setPending(true);
    window.addEventListener(UPDATE_PENDING_EVENT, onPending);
    return () => window.removeEventListener(UPDATE_PENDING_EVENT, onPending);
  }, []);

  // Apply on route change: the SPA already navigated with old code; reloading
  // now lands the user on the SAME page running the NEW code.
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      if (isUpdatePending()) {
        applyUpdateNow();
      }
    }
  }, [location.pathname]);

  if (!pending) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9998]">
      <button
        onClick={applyUpdateNow}
        className="flex items-center gap-2 px-4 py-2.5 bg-neutral-900 text-white text-sm rounded-full shadow-lg hover:bg-neutral-800 transition-colors"
        title="A new version of PropCloudAI has been deployed"
      >
        <RefreshCw className="w-4 h-4 text-[#6eb041]" />
        <span>New version available</span>
        <span className="font-semibold text-[#8bc95e]">Update now</span>
      </button>
    </div>
  );
};

export default UpdateGate;
